import { useRef, useState } from 'react'
import type ReactECharts from 'echarts-for-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSocialAreaData } from '../../../hooks/useSocialAreaData'
import SocialAreaChart from '../../../chart/SocialAreaChart'
import { RiskProfileCard } from '../../RiskProfileCard'
import { ChartQueryState } from '../../ChartQueryState'
import { ChartExportActions } from '../../../chart/ChartExportActions'
import { useExportXlsx } from '../../../hooks/useExportXlsx'
import { useExportChartImage } from '../../../hooks/useExportChartImage'
import type {
  PropertiesQueryParams,
  PropertiesMetricOption,
} from '../../../types/riskProfileProperties.types'

export interface SocialAreaWidgetProps {
  queryParams: PropertiesQueryParams | null
  metricOption: PropertiesMetricOption
  selectedAreaTypesLabel: string
}

export function SocialAreaWidget({
  queryParams,
  metricOption,
  selectedAreaTypesLabel,
}: SocialAreaWidgetProps) {
  const [rankBy, setRankBy] = useState<'percent' | 'count'>('percent')
  const { request, query } = useSocialAreaData(queryParams)
  const { exportXlsx, isExporting } = useExportXlsx(request, '/report/risk-profile/properties/xlsx')
  const chartRef = useRef<ReactECharts>(null)
  const handleExportImage = useExportChartImage(chartRef, 'residents-exposed-by-area')

  return (
    <RiskProfileCard
      title={`${selectedAreaTypesLabel} ranked by residents exposed`}
      onExport={
        <ChartExportActions
          onExportXlsx={exportXlsx}
          onExportImage={handleExportImage}
          isExportingXlsx={isExporting}
          xlsxDisabled={!request}
        />
      }
      className="min-h-96"
    >
      <div className="mb-0 flex items-center gap-2 text-sm text-muted-foreground">
        <span>Rank by</span>
        <Tabs value={rankBy} onValueChange={(value) => setRankBy(value as 'percent' | 'count')}>
          <TabsList className="h-7 p-0.5">
            <TabsTrigger value="percent" className="h-6 px-2 text-xs">
              % exposed
            </TabsTrigger>
            <TabsTrigger value="count" className="h-6 px-2 text-xs">
              # exposed
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <ChartQueryState query={query}>
        {(data) => (
          <SocialAreaChart
            ref={chartRef}
            data={data}
            metricOption={metricOption}
            entityNoun="residents"
            rankBy={rankBy}
          />
        )}
      </ChartQueryState>
    </RiskProfileCard>
  )
}
